import { useState, useEffect, useContext } from "react";
import { IoIosEye, IoIosEyeOff } from "react-icons/io";
import { useNavigate, useParams } from "react-router-dom";
import { passwordValidator, confirmPasswordValidator }  from '../../../utils/validator'
import { DataProvider } from "../../../store/ContextProvider";

export default function AdminEditUsers() {
  const { BeURL, showAlert } = useContext(DataProvider);

  const { id } = useParams();

  const navigate = useNavigate()
 
  const [editUser, setEditUser] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(false);

  // edit password state (optional)
  const [isChange, setIsChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPasswordErrors, setCurrentPasswordErrors] = useState("");
  const [passwordErrors, setPasswordErrors] = useState("");
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // fetch user details 
 useEffect(() => {
    if (!BeURL) return;
   fetch(`${BeURL}/admin/users/${id}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
         setEditUser(data.user);
        setIsChange(false);
        setCurrentPassword("");
        setPassword("");
        setConfirmPassword("");
        setCurrentPasswordErrors("");
        setPasswordErrors("");
        setConfirmPasswordErrors("");
          }
         else {
        showAlert(data?.message || "Cannot fetch user.");
          navigate(-1);
        }
      })
      .catch(() => {
        showAlert("Cannot fetch user.");
        navigate(-1);
      });
  }, [BeURL, id, showAlert, navigate]);


  // when "Change Password" toggles OFF, clear fields & errors
  useEffect(() => {
    if (!isChange) {
      setCurrentPassword("");
      setPassword("");
      setConfirmPassword("");
      setCurrentPasswordErrors("");
      setPasswordErrors("");
      setConfirmPasswordErrors("");
    }
  }, [isChange]);

  // live Validator ONLY when password change is enabled
  useEffect(() => {
    if (!isChange) return;

    if (currentPassword) setCurrentPasswordErrors(passwordValidator(currentPassword) || "");
    else setCurrentPasswordErrors("");

    if (password) setPasswordErrors(passwordValidator(password) || "");
    else setPasswordErrors("");

    if (confirmPassword) {
      setConfirmPasswordErrors(confirmPasswordValidator(password, confirmPassword) || "");
    } else setConfirmPasswordErrors("");
  }, [
    isChange,
    currentPassword,
    password,
    confirmPassword,
    passwordValidator,
    confirmPasswordValidator,
  ]);

  // save handler: updates profile always; password only if enabled
  const updateUsersData = async (id) => {
    // base payload (always update these)
    const payload = {
      fullName: editUser?.fullName,
      email: editUser?.email,
      contact: editUser?.contact,
      
    };

    // if password change is ON, validate and attach fields
    if (isChange) {
      let hasErr = false;
      if (!currentPassword) {
        setCurrentPasswordErrors("This field is required!");
        hasErr = true;
      }
      if (!password) {
        setPasswordErrors("This field is required!");
        hasErr = true;
      }
      if (!confirmPassword) {
        setConfirmPasswordErrors("This field is required!");
        hasErr = true;
      }
      if (password && currentPassword && currentPassword === password) {
        setPasswordErrors("New password must differ from current password.");
        hasErr = true;
      }
      if (currentPasswordErrors || passwordErrors || confirmPasswordErrors) {
        hasErr = true;
      }
      if (hasErr) return;

      Object.assign(payload, {
        isChange: true,
        currentPassword,
        password,
      });
    }

    try {
      setUpdateStatus(true);
      // Single endpoint for both profile & (optional) password change
      const res = await fetch(`${BeURL}/update-EmployerPassword/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data?.success) {
        showAlert(data.message || "Updated.");
        navigate(-1);
        

        // if password changed, clear fields & toggle OFF
        if (isChange) {
          setCurrentPassword("");
          setPassword("");
          setConfirmPassword("");
          setCurrentPasswordErrors("");
          setPasswordErrors("");
          setConfirmPasswordErrors("");
          setIsChange(false);
        }
      } else {
        showAlert(data?.message || "Update failed.");
      }
    } catch (err) {
      console.error("Update error:", err);
      showAlert("Trouble connecting to the server. Try again later.");
    } finally {
      setUpdateStatus(false);
    }
  };

  const manBadge = () => <span className="text-red-600 text-lg font-medium"> *</span>;


  const saveDisabled =
    updateStatus ||
    (isChange &&
      (!currentPassword ||
        !password ||
        !confirmPassword ||
        !!currentPasswordErrors ||
        !!passwordErrors ||
        !!confirmPasswordErrors));

  return (
    <div className="p-6 space-y-4 min-h-[80vh] relative">
     

      {/* Edit Modal */}
      {editUser && (
        <div className="flex items-center justify-center p-4 ">
          <div className="bg-white p-6 rounded-lg w-full max-w-lg space-y-2 border shadow-lg">
            <h3 className="text-xl font-semibold text-primary-400">Edit User Details</h3>
            <form onSubmit={(e) => e.preventDefault()}>
              {["fullName", "email", "contact",'role'].map((field) => (
                <div key={field} className="mb-4">
                  <label className="block mb-1 font-medium">
                    {field.replace(/([A-Z])/g, " $1")}
                  </label>
                 {field === 'role' ? (<p className="w-full border rounded px-3 py-2 ">{editUser?.[field] }</p>) :
                 (
                    <input
                    type="text"
                    value={editUser?.[field] ?? ""}
                    className="w-full border rounded px-3 py-2"
                    onChange={(e) => setEditUser((u) => ({ ...u, [field]: e.target.value }))}
                  />
                 )} 
                  
                </div>
              ))}

              <div className="my-2">
                <input
                  type="checkbox"
                  checked={isChange}
                  onChange={(e) => setIsChange(e.target.checked)}
                  id="changePassword"
                />
                <label className="ml-2" htmlFor="changePassword">
                  Change Password
                </label>
              </div>

              {isChange && (
                <>
                  <div className="my-2">
                    <label className="block text-sm font-medium">
                      Current Password{manBadge()}
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        className="mt-1 block w-full rounded border-gray-300"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        className="text-2xl"
                        onClick={() => setShowCurrentPassword((s) => !s)}
                        aria-label="Toggle current password visibility"
                        title="Toggle visibility"
                      >
                        {showCurrentPassword ? <IoIosEyeOff /> : <IoIosEye />}
                      </button>
                    </div>
                    {currentPasswordErrors && (
                      <p className="px-2 py-1 rounded text-sm text-red-600">
                        {currentPasswordErrors}
                      </p>
                    )}
                  </div>

                  <div className="my-2">
                    <label className="block text-sm font-medium">
                      New Password{manBadge()}
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="mt-1 block w-full rounded border-gray-300"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="text-2xl"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label="Toggle new password visibility"
                        title="Toggle visibility"
                      >
                        {showPassword ? <IoIosEyeOff /> : <IoIosEye />}
                      </button>
                    </div>
                    {passwordErrors && (
                      <p className="px-2 py-1 rounded text-sm text-red-600">{passwordErrors}</p>
                    )}
                  </div>

                  <div className="my-2">
                    <label className="block text-sm font-medium">
                      Confirm Password{manBadge()}
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className="mt-1 block w-full rounded border-gray-300"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="text-2xl"
                        onClick={() => setShowConfirmPassword((s) => !s)}
                        aria-label="Toggle confirm password visibility"
                        title="Toggle visibility"
                      >
                        {showConfirmPassword ? <IoIosEyeOff /> : <IoIosEye />}
                      </button>
                    </div>
                    {confirmPasswordErrors && (
                      <p className="px-2 py-1 rounded text-sm text-red-600">
                        {confirmPasswordErrors}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => updateUsersData(editUser?._id)}
                  className="mt-4 px-4 py-2 bg-primary-400 text-white rounded disabled:opacity-60"
                  disabled={saveDisabled}
                >
                  {updateStatus ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {setEditUser(null)
                    navigate('/list-users')
                  }}
                  className="mt-4 px-4 py-2 bg-slate-200 text-slate-700 rounded"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      
    </div>
  );
}
